import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import SPBackend from '../../services/SPBackend';
import { log } from '../../utils/jsUtils';
import * as R from 'ramda';
import { updateRequests as updateFeedRequests } from '../../reduxStore/modules/feed';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { updateTag, removeTag, addTag, setTags } from '../../reduxStore/modules/tags/actions';

export const tagsApi = createApi({
  reducerPath: 'tagsApi',
  baseQuery: fetchBaseQuery({ baseUrl: '/' }), // Using SPBackend directly
  tagTypes: ['CustomerTags'],
  endpoints: (builder) => ({
    // Add a tag to a customer
    addTagToCustomer: builder.mutation({
      queryFn: async ({ tagId, customerIds, user, showNotification = false }) => {
        try {
          log('[tagsApi] Adding tag to customers:', { 
            tagId, 
            customerIds,
            timestamp: new Date().toISOString(),
            showNotification,
            stackTrace: process.env.NODE_ENV !== 'production' ? new Error().stack : undefined
          });
          const response = await SPBackend.tagCustomers(tagId, customerIds, user);
          
          return { 
            data: {
              ...response.data,
              tagId,
              customerIds,
              showNotification
            }
          };
        } catch (error) {
          log('[tagsApi] Error adding tag:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to add tag'
            } 
          };
        }
      },
      // Optimistic updates
      onQueryStarted: ({ tagId, customerIds, existingRequests, user, showNotification = false }, { dispatch, queryFulfilled }) => {
        // Log what we're attempting to do
        log('[tagsApi.addTagToCustomer] Starting optimistic update', {
          tagId,
          customerIds,
          requestsCount: existingRequests?.length || 0,
          showNotification
        });
        
        // Apply optimistic update to UI
        const patchResult = dispatch(
          tagsApi.util.updateQueryData('getCustomerTags', undefined, (draft) => {
            // Update each customer in the cached data
            customerIds.forEach(customerId => {
              const customer = draft?.find(c => c.id === customerId);
              if (customer) {
                customer.tags = R.union(customer.tags || [], [tagId]);
              }
            });
          })
        );

        // Also update the feed requests if provided
        if (existingRequests && existingRequests.length > 0) {
          // Log before update
          log('[tagsApi.addTagToCustomer] Updating feed requests', {
            existingCount: existingRequests.length,
            customerIds
          });
          
          // Update all requests that have any customer from customerIds
          const updatedRequests = existingRequests.map(req => {
            log('[tagsApi.addTagToCustomer] Processing request:', {
              requestId: req._id,
              lookupTenant: req.lookupTenant,
              customerIds,
              tagId
            });

            // Check if this request has a matching customer by lookupTenant._id
            if (req.lookupTenant && customerIds.includes(req.lookupTenant._id)) {
              log('[tagsApi.addTagToCustomer] Updating request with customer:', {
                requestId: req._id,
                customerId: req.lookupTenant._id,
                existingTags: req.lookupTenant.Tags || []
              });

              log('[tagsApi.addTagToCustomer] Updated request:', {
                ...req,
                lookupTenant: {
                  ...req.lookupTenant,
                  Tags: R.union(req.lookupTenant.Tags || [], [tagId])
                }
              });
              
              // Create a new request object with updated Tags array
              return {
                ...req,
                lookupTenant: {
                  ...req.lookupTenant,
                  Tags: R.union(req.lookupTenant.Tags || [], [tagId])
                }
              };
            }
            
            return req;
          });
          
          // Dispatch to update the feed with the correct action type
          dispatch(updateFeedRequests(updatedRequests));
        }
        
        // Handle the completed API call
        queryFulfilled
          .then((result) => {
            // Only show success notification for user-initiated actions
            if (showNotification) {
              log('[tagsApi.addTagToCustomer] Showing success notification');
              dispatch(setSnackMessage({
                message: 'Tag added successfully',
                level: 'success',
                duration: 2000
              }));
            }
          })
          .catch(() => {
            patchResult.undo();
            
            // If we updated the feed, we also need to revert that
            if (existingRequests && existingRequests.length > 0) {
              dispatch(updateFeedRequests(existingRequests));
            }
          });
      },
      invalidatesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    }),
    
    // Remove a tag from a customer
    removeTagFromCustomer: builder.mutation({
      queryFn: async ({ tagId, customerIds, user, showNotification = true }) => {
        try {
          log('[tagsApi] Removing tag from customers:', { tagId, customerIds });
          const response = await SPBackend.removeTagFromCustomers(tagId, customerIds, user);
          
          return { 
            data: {
              ...response.data,
              tagId,
              customerIds,
              showNotification
            }
          };
        } catch (error) {
          log('[tagsApi] Error removing tag:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to remove tag'
            } 
          };
        }
      },
      // Optimistic updates
      onQueryStarted: async({ tagId, customerIds, user, showNotification = true }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const existingRequests = state.feed.requests || [];

        log('[tagsApi.removeTagFromCustomer] Starting optimistic update', {
          tagId,
          customerIds,
          requestsCount: existingRequests?.length || 0,
          showNotification
        });

        // ALWAYS perform optimistic updates to ensure UI is immediately updated
        // Create a reference to hold our rollback function
        let rollbackAction = null;

        // Get current feed state to save for potential rollback
        const originalRequests = [...existingRequests];

        // Create rollback function that restores requests
        rollbackAction = () => {
          dispatch(updateFeedRequests(originalRequests));
        };
        
        // Update all requests that have any customer from customerIds
        const updatedRequests = existingRequests.map(req => {
          // Check if this request has a matching customer by lookupTenant._id
          if (req.lookupTenant && customerIds.includes(req.lookupTenant._id)) {
            log('[tagsApi.removeTagFromCustomer] Updating request with customer:', {
              requestId: req._id,
              customerId: req.lookupTenant._id,
              existingTags: req.lookupTenant.Tags || []
            });
            
            // Create a new request object with filtered Tags array
            return {
              ...req,
              lookupTenant: {
                ...req.lookupTenant,
                Tags: (req.lookupTenant.Tags || []).filter(existingTagId => {
                  return String(existingTagId) !== String(tagId);
                })
              }
            };
          }
          
          return req;
        });

        // Dispatch the updated requests (single source of truth)
        dispatch(updateFeedRequests(updatedRequests));
        
        // Handle the completed API call
        queryFulfilled
          .then(() => {
            // Only show success notification for user-initiated actions
            if (showNotification) {
              dispatch(setSnackMessage({
                message: 'Tag removed successfully',
                level: 'success',
                duration: 2000
              }));
            }
          })
          .catch(() => {
            // If we did our own Redux update, we need to revert it on error
            if (rollbackAction) {
              rollbackAction();
            }
          });
      },
      invalidatesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    }),
    
    // Edit a tag
    editTag: builder.mutation({
      queryFn: async ({ tagData, user, showNotification = true }) => {
        try {
          log('[tagsApi] Editing tag:', { tagData });
          const response = await SPBackend.editTag(tagData, user);
          
          return { 
            data: {
              ...response.data,
              tagData,
              showNotification
            }
          };
        } catch (error) {
          log('[tagsApi] Error editing tag:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to edit tag'
            } 
          };
        }
      },
      // Optimistic updates
      onQueryStarted: async ({ tagData, user, showNotification = true }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const existingRequests = state.feed?.requests || [];
        const currentTags = state.tags?.tags || [];

        log('[tagsApi.editTag] Starting optimistic update', {
          tagData,
          requestsCount: existingRequests?.length || 0,
          showNotification
        });

        // Find and store the original tag data for rollback
        const originalTag = currentTags.find(tag => tag._id === tagData._id);
        log('[tagsApi.editTag] Original tag for rollback:', originalTag);

        // Get current state for potential rollback
        const originalRequests = [...existingRequests];

        // Create rollback function for feed data
        const rollbackFeedAction = () => {
          dispatch(updateFeedRequests(originalRequests));
        };
        
        // Update the tag in the tags Redux store (optimistic update)
        log('[tagsApi.editTag] Dispatching updateTag action with:', tagData);
        dispatch(updateTag(tagData));
        log('[tagsApi.editTag] updateTag action dispatched');
        
        // Update all requests that reference this tag in their Tags array
        if (existingRequests.length > 0) {
          const updatedRequests = existingRequests.map(req => {
            if (req.lookupTenant && req.lookupTenant.Tags && req.lookupTenant.Tags.includes(tagData._id)) {
              log('[tagsApi.editTag] Request references this tag, no structural change needed for:', {
                requestId: req._id,
                customerId: req.lookupTenant._id,
                tagId: tagData._id
              });
              // Note: We don't need to change the Tags array structure since we're just editing the tag properties,
              // not adding/removing tags from customers. The TagRenderer will automatically pick up the updated
              // tag data from the tags store when it renders.
            }
            return req;
          });

          // Dispatch the updated requests (single source of truth)
          dispatch(updateFeedRequests(updatedRequests));
        }
        
        // Handle the completed API call
        queryFulfilled
          .then((result) => {
            // Show success notification
            if (showNotification) {
              dispatch(setSnackMessage({
                message: 'Tag updated successfully',
                level: 'success',
                duration: 2000
              }));
            }
            
            // Update with the actual response data from server
            if (result.data && result.data.tag) {
              dispatch(updateTag(result.data.tag));
            }
          })
          .catch((error) => {
            // Rollback the optimistic update to the original tag state
            if (originalTag) {
              log('[tagsApi.editTag] Rolling back to original tag:', originalTag);
              dispatch(updateTag(originalTag));
            } else {
              log('[tagsApi.editTag] Warning: No original tag found for rollback');
            }
            
            // Rollback feed data if needed
            if (rollbackFeedAction) {
              rollbackFeedAction();
            }
            
            // Show error notification
            dispatch(setSnackMessage({
              message: error.data || 'Failed to update tag',
              level: 'error',
              duration: 3000
            }));
          });
      },
      invalidatesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    }),

    // Archive (delete) a tag
    archiveTag: builder.mutation({
      queryFn: async ({ tagId, user, showNotification = true }) => {
        try {
          log('[tagsApi] Archiving tag:', { tagId });
          const response = await SPBackend.archiveTag(tagId, user);
          
          return { 
            data: {
              ...response.data,
              tagId,
              showNotification
            }
          };
        } catch (error) {
          log('[tagsApi] Error archiving tag:', error);
          return { 
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to archive tag'
            } 
          };
        }
      },
      // Optimistic updates
      onQueryStarted: async ({ tagId, user, showNotification = true }, { dispatch, getState, queryFulfilled }) => {
        const state = getState();
        const existingRequests = state.feed?.requests || [];
        const currentTags = state.tags?.tags || [];

        log('[tagsApi.archiveTag] Starting optimistic update', {
          tagId,
          requestsCount: existingRequests?.length || 0,
          showNotification
        });
        
        // Find and store the original tag data and position for rollback
        const originalTagIndex = currentTags.findIndex(tag => tag._id === tagId);
        const originalTag = currentTags[originalTagIndex];
        log('[tagsApi.archiveTag] Original tag for rollback:', originalTag, 'at index:', originalTagIndex);
        
        if (!originalTag || originalTagIndex === -1) {
          log('[tagsApi.archiveTag] Warning: No original tag found with ID:', tagId);
          return;
        }
        
        // Get current state for potential rollback
        const originalRequests = [...existingRequests];

        // Create rollback function for feed data
        const rollbackFeedAction = () => {
          dispatch(updateFeedRequests(originalRequests));
        };
        
        // Remove the tag from the tags Redux store (optimistic update)
        log('[tagsApi.archiveTag] Dispatching removeTag action for:', originalTag);
        dispatch(removeTag('', originalTag)); // customerId is empty since we're removing from the global tags list
        log('[tagsApi.archiveTag] removeTag action dispatched');
        
        // Update all requests that reference this tag in their Tags array
        if (existingRequests.length > 0) {
          const updatedRequests = existingRequests.map(req => {
            if (req.lookupTenant && req.lookupTenant.Tags && req.lookupTenant.Tags.includes(tagId)) {
              log('[tagsApi.archiveTag] Removing tag from request:', {
                requestId: req._id,
                customerId: req.lookupTenant._id,
                tagId: tagId
              });
              
              // Create a new request object with filtered Tags array
              return {
                ...req,
                lookupTenant: {
                  ...req.lookupTenant,
                  Tags: (req.lookupTenant.Tags || []).filter(existingTagId => {
                    return String(existingTagId) !== String(tagId);
                  })
                }
              };
            }
            return req;
          });

          // Dispatch the updated requests (single source of truth)
          dispatch(updateFeedRequests(updatedRequests));
        }
        
        // Handle the completed API call
        queryFulfilled
          .then((result) => {
            // Show success notification
            if (showNotification) {
              dispatch(setSnackMessage({
                message: result.data?.message || 'Tag archived successfully',
                level: 'success',
                duration: 2000
              }));
            }
          })
          .catch((error) => {
            // Rollback the optimistic update by re-adding the tag at its original position
            if (originalTag && originalTagIndex !== -1) {
              log('[tagsApi.archiveTag] Rolling back - re-adding tag at position:', originalTagIndex, originalTag);
              
              // Get current state after the optimistic removal
              const currentState = getState();
              const currentTagsAfterRemoval = currentState.tags?.tags || [];
              
              // Restore the tag at its original position
              const restoredTags = [...currentTagsAfterRemoval];
              restoredTags.splice(originalTagIndex, 0, originalTag);
              
              // Dispatch the setTags action to restore the exact tags array with proper positioning
              dispatch(setTags(restoredTags));
              
              log('[tagsApi.archiveTag] Tag restored at original position');
            } else {
              log('[tagsApi.archiveTag] Warning: No original tag found for rollback');
            }
            
            // Rollback feed data if needed
            if (rollbackFeedAction) {
              rollbackFeedAction();
            }
            
            // Show error notification
            dispatch(setSnackMessage({
              message: error.data || 'Failed to archive tag',
              level: 'error',
              duration: 3000
            }));
          });
      },
      invalidatesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    }),

    // Create a new tag
    createTag: builder.mutation({
      queryFn: async ({ name, color, user, showNotification = true }) => {
        try {
          log('[tagsApi] Creating tag:', { name, color });
          const response = await SPBackend.setTag({ Name: name, Color: color }, user);

          return {
            data: {
              ...response.data,
              showNotification
            }
          };
        } catch (error) {
          log('[tagsApi] Error creating tag:', error);
          return {
            error: {
              status: error.status || 'FETCH_ERROR',
              data: error.response ? error.response.data : error.message || 'Failed to create tag'
            }
          };
        }
      },
      onQueryStarted: async ({ name, color, user, showNotification = true }, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then((result) => {
            // Add the new tag to Redux store
            if (result.data && result.data.tag) {
              dispatch(addTag('', result.data.tag));
            }

            if (showNotification) {
              dispatch(setSnackMessage({
                message: 'Tag created successfully',
                level: 'success',
                duration: 2000
              }));
            }
          })
          .catch((error) => {
            dispatch(setSnackMessage({
              message: error.data || 'Failed to create tag',
              level: 'error',
              duration: 3000
            }));
          });
      },
      invalidatesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    }),

    // Get all tags for a customer (utility endpoint)
    getCustomerTags: builder.query({
      queryFn: async ({ customerId, user }) => {
        try {
          // This would be your actual endpoint to get customer tags
          // For now we're just returning a placeholder
          return { data: [] };
        } catch (error) {
          return { error: error.message || 'Failed to fetch customer tags' };
        }
      },
      providesTags: (result) => [{ type: 'CustomerTags', id: 'LIST' }]
    })
  })
});

export const {
  useAddTagToCustomerMutation,
  useRemoveTagFromCustomerMutation,
  useEditTagMutation,
  useArchiveTagMutation,
  useGetCustomerTagsQuery,
  useCreateTagMutation
} = tagsApi; 